/**
 * depositsTable.js — קיבוץ תנועות הפקדה (fund.flows.deposits) לשורות טבלה.
 *
 * כל תנועה גולמית (PerutHafkadotMetchilatShana) היא רכיב בודד (פיצויים / תגמולים
 * עובד / תגמולים מעביד / קה"ש...). כאן מקבצים לפי (מעסיק + חודש שכר + תאריך ערך +
 * סוג הפקדה) ומסובבים את הסכומים לעמודות: הפקדה עובד / הפקדה מעסיק / פיצויים — כמו
 * שמוצג בתוכנות אחרות. שורת "ניוד כספים" / "מענק שנים עודפות" (שם המעסיק) נשמרת כפי
 * שהיא, כך שברור מה שוטף ומה חד-פעמי.
 */

// SUG-HAFRASHA → עמודה. עובד: תגמולים עובד(2)/קה"ש עובד(8)/תגמולים פרט(10).
// מעסיק: תגמולים מעביד(3)/תגמולים47(4)/קה"ש מעביד(9). פיצויים: 1.
const OVED_HAFRASHA   = new Set(["2", "8", "10"]);
const MAAVID_HAFRASHA = new Set(["3", "4", "9"]);

// סיווג רכיב לעמודה. עובד על קוד SUG-HAFRASHA אם קיים, אחרת על תווית (`type`) —
// כדי שגם נתונים ישנים (שנותחו לפני שהוספנו את הקוד) יתמלאו נכון בלי ייבוא מחדש.
function bucketOf(d) {
  if (d.sugHafrasha === "1") return "pitzuyim";
  if (OVED_HAFRASHA.has(d.sugHafrasha)) return "oved";
  if (MAAVID_HAFRASHA.has(d.sugHafrasha)) return "maavid";
  const t = d.type || "";
  if (t.includes("פיצויים")) return "pitzuyim";
  if (t.includes("מעביד") || t.includes("מעסיק") || t.includes("47")) return "maavid";
  if (t.includes("תגמולים") || t.includes('קה"ש') || t.includes("עובד") || t.includes("פרט")) return "oved";
  return "other";
}

// ISO (YYYY-MM-DD) → DD/MM/YYYY, לגיבוי כשאין dateRaw (נתונים ישנים).
function isoToDisplay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

export function groupDeposits(deposits = []) {
  const map = new Map();
  for (const d of deposits) {
    // נירמול שדות — עובד גם על מבנה ישן של flows (בלי depositType/dateRaw/maasik).
    const depositType = d.depositType || (d.isTransfer ? "ניוד כספים" : "שוטפת");
    const date = d.dateRaw || isoToDisplay(d.valueDate);
    const key = [d.maasik || "", d.chodeshSachar || "", date || "", depositType].join("|");
    if (!map.has(key)) {
      map.set(key, {
        depositType,
        maasik:        d.maasik || null,
        chodeshSachar: d.chodeshSachar || null,
        date:          date || null,
        _sortDate:     d.valueDate || "",
        salary:        null,
        oved: 0, maavid: 0, pitzuyim: 0, other: 0, total: 0,
        isTransfer:    !!d.isTransfer,
      });
    }
    const row = map.get(key);
    if (d.salary && !row.salary) row.salary = d.salary;
    if (d.valueDate && d.valueDate > row._sortDate) row._sortDate = d.valueDate;
    const amt = d.amount || 0;
    row.total += amt;
    row[bucketOf(d)] += amt;
  }
  // החדש ביותר למעלה
  return Array.from(map.values()).sort((a, b) => (b._sortDate || "").localeCompare(a._sortDate || ""));
}

// DD/MM/YYYY → ISO (YYYY-MM-DD), למיון.
function ddmmyyyyToIso(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(String(s || ""));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
}

// המרת "הפקדה אחרונה" (PirteiHafkadaAchrona) לפריטי הפקדה — לקרנות שאין להן תנועות
// מתוארכות (PerutHafkadotMetchilatShana), כדי שהטבלה תוצג באופן אחיד לכל הקרנות.
export function lastDepositToDeposits(lastDeposit = []) {
  const out = [];
  for (const row of lastDeposit || []) {
    const rowType = row.sugHafkada === "3" ? "ניוד כספים"
      : row.sugHafkada === "2" ? "חד פעמית" : null;
    for (const b of row.breakdown || []) {
      if (!b.amount) continue;
      out.push({
        type:          b.sugHafrasha,
        amount:        b.amount,
        salary:        b.salary || null,
        payer:         b.payer,
        isTransfer:    !!b.isTransfer,
        depositType:   rowType || (b.isTransfer ? "ניוד כספים" : "שוטפת"),
        dateRaw:       row.date || null,
        chodeshSachar: row.period || null,
        valueDate:     ddmmyyyyToIso(row.date),
        maasik:        null,
      });
    }
  }
  return out;
}

// אחוז הפקדה מתוך השכר (אם ידוע) — להצגה ליד הסכום.
export function pctOfSalary(amount, salary) {
  if (!salary || salary <= 0 || !amount) return null;
  return Math.round((amount / salary) * 1000) / 10; // עיגול לעשירית האחוז
}
